import React, { FC } from 'react'
import {
  Box,
  Typography,
  Switch,
  Stack,
  Paper,
  Container,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
} from '@mui/material'
import { BackButton } from '../../../../components/molecules/BackButton'
import {
  useUserSettingsQuery,
  useUpdateUserSettingsMutation,
} from '../../../../features/users'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../../../contexts/ToastContext'

export const TMNOcrSettingsPage: FC = () => {
  const { data: settings = null, isPending, isError } = useUserSettingsQuery()
  const updateMutation = useUpdateUserSettingsMutation()
  const navigate = useNavigate()
  const { showToast } = useToast()

  if (isPending) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography>設定を読み込み中...</Typography>
      </Container>
    )
  }

  if (isError || !settings) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography color="error">設定の読み込みに失敗しました</Typography>
      </Container>
    )
  }

  // TODO: 設定変更の有効化時に復活
  // const handleCsvSettingChange = (value: number) => {
  //   updateMutation.mutate(
  //     { when_to_save_csv: value },
  //     {
  //       onSuccess: () => {
  //         setSuccessMessage('CSV保存設定を更新しました')
  //         setErrorMessage(null)
  //       },
  //       onError: (err) => {
  //         console.error('Failed to update CSV setting:', err)
  //         setErrorMessage('CSV保存設定の更新に失敗しました')
  //         setSuccessMessage(null)
  //       },
  //     }
  //   )
  // }

  // const handleQrDirectlyChange = (checked: boolean) => {
  //   updateMutation.mutate(
  //     { read_qr_directly: checked },
  //     {
  //       onSuccess: () => {
  //         setSuccessMessage('QR直接読み取り設定を更新しました')
  //         setErrorMessage(null)
  //       },
  //       onError: (err) => {
  //         console.error('Failed to update QR direct reading setting:', err)
  //         setErrorMessage('QR直接読み取り設定の更新に失敗しました')
  //         setSuccessMessage(null)
  //       },
  //     }
  //   )
  // }

  const getRecognitionModeFromThreshold = (threshold: number): string => {
    if (threshold <= 0.6) return 'aggressive'
    if (threshold <= 0.8) return 'normal'
    return 'conservative'
  }

  const getThresholdFromMode = (mode: string): number => {
    switch (mode) {
      case 'aggressive':
        return 0.6
      case 'normal':
        return 0.8
      case 'conservative':
        return 0.9
      default:
        return 0.8
    }
  }

  const handleRecognitionModeChange = (mode: string) => {
    const threshold = getThresholdFromMode(mode)
    updateMutation.mutate(
      { medication_number_confidence_threshold: threshold },
      {
        onSuccess: () => {
          showToast('医薬品数字認識モードを更新しました', 'success')
        },
        onError: (err) => {
          console.error('Failed to update recognition mode:', err)
          showToast('医薬品数字認識モードの更新に失敗しました', 'error', 5000)
        },
      }
    )
  }

  // TODO: 設定変更の有効化時に復活
  // const handleOcrModelTypeChange = (value: string) => {
  //   updateMutation.mutate(
  //     { ocr_model_type: value },
  //     {
  //       onSuccess: () => {
  //         setSuccessMessage('OCRモデルタイプを更新しました')
  //         setErrorMessage(null)
  //       },
  //       onError: (err) => {
  //         console.error('Failed to update OCR model type:', err)
  //         setErrorMessage('OCRモデルタイプの更新に失敗しました')
  //         setSuccessMessage(null)
  //       },
  //     }
  //   )
  // }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Stack spacing={3}>
        {/* 結果書き込み設定セクション */}
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 'medium',
              color: 'primary.dark',
              mb: 3,
              pb: 1,
              borderBottom: '1px solid',
              borderBottomColor: 'primary.dark',
            }}
          >
            結果書き込み設定
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.disabled', mb: 2 }}>
            現在変更いただけません
          </Typography>

          <Stack spacing={3} sx={{ opacity: 0.5 }}>
            {/* CSV保存タイミング */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 'medium', mb: 0.5 }}
                >
                  CSV保存タイミング
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  処方箋読み取り時のCSVファイル保存タイミングを設定します
                </Typography>
              </Box>
              <Tooltip title="現在変更いただけません">
                <FormControl sx={{ minWidth: 200 }} disabled>
                  <InputLabel id="csv-timing-label">保存タイミング</InputLabel>
                  <Select
                    labelId="csv-timing-label"
                    id="csv-timing-select"
                    value={settings.when_to_save_csv.value}
                    label="保存タイミング"
                  >
                    {settings.when_to_save_csv.choices.map((choice) => (
                      <MenuItem key={choice.value} value={choice.value}>
                        {choice.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Tooltip>
            </Stack>

            <Divider />

            {/* QR直接読み取り */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 'medium', mb: 0.5 }}
                >
                  QR直接読み取り
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  処方箋QRがあれば直接読取り、CSVを出力します
                </Typography>
              </Box>
              <Tooltip title="現在変更いただけません">
                <span>
                  {/* FIXME: read_qr_directlyの値が正しく取得できていない可能性あり。API修正後にtrueハードコードを除去すること */}
                  <Switch color="primary" checked={true} disabled />
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </Paper>

        {/* OCRモデル設定セクション */}
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 'medium',
              color: 'primary.dark',
              mb: 3,
              pb: 1,
              borderBottom: '1px solid',
              borderBottomColor: 'primary.dark',
            }}
          >
            OCRモデル設定
          </Typography>

          <Stack spacing={3}>
            {/* 医薬品数字認識モード */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 'medium', mb: 0.5 }}
                >
                  医薬品数字認識モード
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  数字認識の精度レベルを選択してください。攻めは多く認識しますが誤認識のリスクがあり、保守的は確実性を重視しますが認識漏れの可能性があります。
                </Typography>
              </Box>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel id="recognition-mode-label">認識モード</InputLabel>
                <Select
                  labelId="recognition-mode-label"
                  id="recognition-mode-select"
                  value={getRecognitionModeFromThreshold(
                    settings.medication_number_confidence_threshold || 0.8
                  )}
                  label="認識モード"
                  onChange={(e) => handleRecognitionModeChange(e.target.value)}
                >
                  <MenuItem value="aggressive">
                    攻め（多くの数字を認識）
                  </MenuItem>
                  <MenuItem value="normal">通常（バランス重視）</MenuItem>
                  <MenuItem value="conservative">
                    保守的（確実性を重視）
                  </MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <Divider />

            {/* OCRモデルタイプ */}
            <Typography variant="body2" sx={{ color: 'text.disabled', mt: 1 }}>
              現在変更いただけません
            </Typography>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
              sx={{ opacity: 0.5 }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 'medium', mb: 0.5 }}
                >
                  OCRモデルタイプ
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  使用するOCRモデルを選択してください。安定版モデルが一般的に推奨されます。β版モデルは新機能のテスト版です。
                </Typography>
              </Box>
              <Tooltip title="現在変更いただけません">
                <FormControl sx={{ minWidth: 200 }} disabled>
                  <InputLabel id="ocr-model-type-label">
                    モデルタイプ
                  </InputLabel>
                  <Select
                    labelId="ocr-model-type-label"
                    id="ocr-model-type-select"
                    value={settings.ocr_model_type?.value || 'standard'}
                    label="モデルタイプ"
                  >
                    <MenuItem value="standard">安定版モデル（推奨）</MenuItem>
                    <MenuItem value="latest">最新モデル（最新の機能）</MenuItem>
                    <MenuItem value="high_accuracy">
                      β版モデル（テスト版）
                    </MenuItem>
                  </Select>
                </FormControl>
              </Tooltip>
            </Stack>
          </Stack>
        </Paper>
      </Stack>

      {/* ナビゲーションボタン */}
      <BackButton
        text="処方箋画面へ戻る"
        onClick={() => navigate('/')}
        variant="topLeft"
      />

      <BackButton
        text="管理ポータルに戻る"
        onClick={() => navigate('/portal')}
      />
    </Container>
  )
}

export default TMNOcrSettingsPage
